import { trackUmamiEvent } from '@/helpers/analytics';
import { XIcon } from './XIcon';

interface UsernameWithXLinkProps {
  username: string;
  twitterUsername?: string | null;
  showTwitterUsername?: boolean;
  className?: string;
  iconClassName?: string;
  /** Use when rendered inside <a> or <Link> to avoid invalid nested anchor tags */
  insideLink?: boolean;
  /** Source context for analytics (e.g. leaderboard, home_top_managers, duel_voters, profile_own, profile_other, onboarding) */
  trackSource?: string;
}

/** Displays username with optional X icon link when user has visible Twitter/X profile */
export function UsernameWithXLink({
  username,
  twitterUsername,
  showTwitterUsername = true,
  className = '',
  iconClassName = 'h-3.5 w-3.5',
  insideLink = false,
  trackSource,
}: UsernameWithXLinkProps) {
  const showX = !!twitterUsername && showTwitterUsername !== false;
  const xUrl = showX ? `https://x.com/${twitterUsername}` : '';

  const handleXClick = () => {
    if (trackSource) {
      trackUmamiEvent('x_profile_click', { source: trackSource, username, twitterUsername });
    }
  };

  const linkClassName = 'inline-flex items-center flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors';
  const buttonClassName = `${linkClassName} bg-transparent border-0 p-0 cursor-pointer !min-h-0 !min-w-0`;
  const ariaLabel = `${username} on X`;

  return (
    <span className={`inline-flex items-center gap-1.5 truncate ${className}`}>
      <span className="truncate">{username}</span>
      {showX &&
        (insideLink ? (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleXClick();
              window.open(xUrl, '_blank', 'noopener,noreferrer');
            }}
            className={buttonClassName}
            aria-label={ariaLabel}
          >
            <XIcon className={iconClassName} />
          </button>
        ) : (
          <a
            href={xUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClassName}
            aria-label={ariaLabel}
            onClick={handleXClick}
          >
            <XIcon className={iconClassName} />
          </a>
        ))}
    </span>
  );
}
